import { ViewBooking, ViewBookingStatusEnum } from "collins-js-sdk";
import React, { useEffect, useMemo, useState } from "react";
import {
  addOrUpdateBooking,
  deleteBooking,
  restoreBooking
} from "../../../../../../APIs/Booking/UpdateBooking";
import { addOrUpdateCustomer } from "../../../../../../APIs/Customer/updateCustomer";
import {
  confirmEnquiryRequest,
  rejectEnquiryRequest
} from "../../../../../../APIs/Enquiries/EnquiryOperations";
import { useAppContext } from "../../../../../../hooks/useAppContext";
import { useCustomerContext } from "../../../../../../hooks/useCustomerContext";
import { useDiaryContext } from "../../../../../../hooks/useDiaryContext";
import { useNotificationsContext } from "../../../../../../hooks/useNotificationsContext";
import usePermissions from "../../../../../../hooks/usePermissions";
import { EDSBadge } from "../../../../../eds/EDSBadge";
import { EDSButton } from "../../../../../eds/EDSButton";
import { Checkbox } from "../../../../../ui/checkbox";
import { Label } from "../../../../../ui/label";
import { getAllCustomerLabels } from "../../Labels/BookingLabels";
import DeleteBookingDialog, {
  type DeleteReason
} from "../DeleteBooking/DeleteBookingDialog";
import { updateBooking } from "../SelectedBookingManageController";

interface ManageFooterProps {
  handleUndo: () => void;
  isReopenLoading: boolean;
}

const ManageFooter = ({ handleUndo, isReopenLoading }: ManageFooterProps) => {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [isRejectLoading, setIsRejectLoading] = useState(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isRestoreLoading, setIsRestoreLoading] = useState(false);
  const { venueGroupConfig } = useAppContext();
  const diaryContext = useDiaryContext();
  const {
    selectedBooking,
    bookings,
    isDeletePending,
    setDiaryContext,
    openedFromEnquiry,
    selectedEnquiry
  } = diaryContext;
  const { selectedCustomer, setSelectedCustomer } = useCustomerContext();
  const notificationsContext = useNotificationsContext();
  const { roles, capabilities } = usePermissions();

  const allCustomerLabels = useMemo(
    () =>
      diaryContext.venueConfig && venueGroupConfig
        ? getAllCustomerLabels({
            venueConfig: diaryContext.venueConfig,
            venueGroupConfig
          })
        : [],
    [diaryContext.venueConfig, venueGroupConfig]
  );

  const noShowLabels = useMemo(
    () => allCustomerLabels.filter((label) => label.type === "no_show"),
    [allCustomerLabels]
  );

  useEffect(() => {
    if (selectedCustomer && selectedCustomer.labels) {
      const customerNoShowLabels = selectedCustomer.labels.filter((labelId) =>
        noShowLabels.some((label) => label.id === labelId)
      );
      setSelectedReasons(customerNoShowLabels);
    } else {
      setSelectedReasons([]);
    }
  }, [selectedCustomer, noShowLabels]);

  if (
    !selectedBooking ||
    !bookings ||
    !diaryContext.venueConfig ||
    !venueGroupConfig
  ) {
    return null;
  }

  const saveChanges = (bookingPropsToSave: Partial<ViewBooking>) => {
    const { updatedBookings, updatedBooking } = updateBooking({
      selectedBooking,
      bookings,
      bookingPropsToSave
    });

    setDiaryContext({ bookings: updatedBookings });
    addOrUpdateBooking({
      booking: updatedBooking,
      diaryContext
    });
  };

  const toggleCustomerLabel = (
    labelId: string,
    labels: string[] = []
  ): string[] => {
    const labelInList = labels.indexOf(labelId);
    const updatedLabels = [...labels];

    if (labelInList > -1) {
      updatedLabels.splice(labelInList, 1);
    } else {
      updatedLabels.push(labelId);
    }

    return updatedLabels;
  };

  const setRejected = async () => {
    setIsRejectLoading(true);

    try {
      if (openedFromEnquiry && selectedEnquiry?._id) {
        const updatedEnquiry = await rejectEnquiryRequest(selectedEnquiry._id);
        const mergedEnquiry = { ...selectedEnquiry, ...updatedEnquiry };
        const { updatedBookings } = updateBooking({
          selectedBooking,
          bookings,
          bookingPropsToSave: { status: ViewBookingStatusEnum.Rejected }
        });
        setDiaryContext({
          bookings: updatedBookings,
          selectedEnquiry: mergedEnquiry
        });
      } else {
        const updatedBookingForAPI = {
          ...selectedBooking,
          status: ViewBookingStatusEnum.Rejected
        };

        const response = await addOrUpdateBooking({
          booking: updatedBookingForAPI
        });

        if (response && !response.unsaved && response.id) {
          const { updatedBookings } = updateBooking({
            selectedBooking,
            bookings,
            bookingPropsToSave: { status: ViewBookingStatusEnum.Rejected }
          });
          setDiaryContext({ bookings: updatedBookings });
        } else {
          notificationsContext.addNotification({
            heading: "Failed to reject booking",
            autoClose: 4000,
            toastType: "error"
          });
        }
      }
    } catch {
      notificationsContext.addNotification({
        heading: "Failed to reject booking",
        autoClose: 4000,
        toastType: "error"
      });
    } finally {
      setIsRejectLoading(false);
    }
  };

  const setConfirmed = async () => {
    setIsConfirmLoading(true);

    try {
      if (openedFromEnquiry && selectedEnquiry?._id) {
        const updatedEnquiry = await confirmEnquiryRequest(selectedEnquiry._id);
        const mergedEnquiry = { ...selectedEnquiry, ...updatedEnquiry };
        const { updatedBookings } = updateBooking({
          selectedBooking,
          bookings,
          bookingPropsToSave: { status: ViewBookingStatusEnum.Complete }
        });
        setDiaryContext({
          bookings: updatedBookings,
          selectedEnquiry: mergedEnquiry
        });
      } else {
        const updatedBookingForAPI = {
          ...selectedBooking,
          status: ViewBookingStatusEnum.Complete
        };

        const response = await addOrUpdateBooking({
          booking: updatedBookingForAPI
        });

        if (response && !response.unsaved && response.id) {
          const { updatedBookings } = updateBooking({
            selectedBooking,
            bookings,
            bookingPropsToSave: { status: ViewBookingStatusEnum.Complete }
          });
          setDiaryContext({ bookings: updatedBookings });
        } else {
          notificationsContext.addNotification({
            heading: "Failed to confirm booking",
            autoClose: 4000,
            toastType: "error"
          });
        }
      }
    } catch {
      notificationsContext.addNotification({
        heading: "Failed to confirm booking",
        autoClose: 4000,
        toastType: "error"
      });
    } finally {
      setIsConfirmLoading(false);
    }
  };

  const noShow = selectedBooking.actualGuests === 0;
  const setNoShow = () => {
    if (noShow) return;
    saveChanges({
      actualGuests: 0,
      currentStage: "no-show"
    });
  };

  const handleNoShowReasonSelect = (labelId: string) => {
    if (!selectedCustomer) return;

    const currentLabels = selectedCustomer.labels || [];
    const updatedLabels = toggleCustomerLabel(labelId, currentLabels);
    const updatedReasons = toggleCustomerLabel(labelId, selectedReasons);

    setSelectedReasons(updatedReasons);

    const updatedCustomer = { ...selectedCustomer, labels: updatedLabels };
    setSelectedCustomer(updatedCustomer);
    addOrUpdateCustomer(updatedCustomer);
  };

  const setDoubleConfirmed = (value: boolean) => {
    saveChanges({ doubleConfirmed: value });
  };

  const userCanDeleteBookings =
    roles.booking.delete ||
    capabilities.deleteVenueGroupBookings.thisVenueGroup ||
    capabilities.deleteVenueBookings.thisVenue;

  const isNewOrInProgress =
    selectedBooking.status === ViewBookingStatusEnum.New ||
    selectedBooking.status === ViewBookingStatusEnum.InProgress;

  const isConfirmed = selectedBooking.status === ViewBookingStatusEnum.Complete;

  const isRejected = selectedBooking.status === ViewBookingStatusEnum.Rejected;

  const isDeleted = selectedBooking.status === ViewBookingStatusEnum.Deleted;

  const canConfirm = isNewOrInProgress;
  const canReject = isNewOrInProgress || isConfirmed;
  const canReopen = isConfirmed || isRejected;
  const canDelete = (isConfirmed || isNewOrInProgress) && userCanDeleteBookings;
  const canRestore = isDeleted && !isDeletePending;

  const revertOptimisticDelete = () => {
    const { updatedBookings: revertedBookings } = updateBooking({
      selectedBooking,
      bookings,
      bookingPropsToSave: { status: selectedBooking.status }
    });
    setDiaryContext({ bookings: revertedBookings, isDeletePending: false });
    notificationsContext.addNotification({
      heading: "Failed to delete booking",
      autoClose: 4000,
      toastType: "error"
    });
  };

  const handleDeleteBooking = async (reason: DeleteReason) => {
    if (!selectedBooking.id || !bookings) return;
    setIsDeleteLoading(true);

    try {
      // Optimistically update the local booking state
      const { updatedBookings } = updateBooking({
        selectedBooking,
        bookings,
        bookingPropsToSave: { status: ViewBookingStatusEnum.Deleted }
      });

      setDiaryContext({ bookings: updatedBookings, isDeletePending: true });

      const success = await deleteBooking(selectedBooking.id, reason);

      if (success) {
        setDiaryContext({ isDeletePending: false });
        setIsDeleteDialogOpen(false);
        notificationsContext.addNotification({
          heading: "Booking deleted successfully",
          autoClose: 4000,
          toastType: "success"
        });
      } else {
        revertOptimisticDelete();
      }
    } catch {
      revertOptimisticDelete();
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleRestoreBooking = async () => {
    if (!selectedBooking.id || !bookings) return;
    setIsRestoreLoading(true);

    try {
      const restoredBooking = await restoreBooking(selectedBooking.id);

      if (restoredBooking) {
        const restoredStatus =
          restoredBooking.status ?? ViewBookingStatusEnum.InProgress;
        const { updatedBookings } = updateBooking({
          selectedBooking,
          bookings,
          bookingPropsToSave: { status: restoredStatus }
        });
        setDiaryContext({ bookings: updatedBookings });
        notificationsContext.addNotification({
          heading: "Booking restored successfully",
          autoClose: 4000,
          toastType: "success"
        });
      } else {
        notificationsContext.addNotification({
          heading: "Failed to restore booking",
          autoClose: 4000,
          toastType: "error"
        });
      }
    } catch {
      notificationsContext.addNotification({
        heading: "Failed to restore booking",
        autoClose: 4000,
        toastType: "error"
      });
    } finally {
      setIsRestoreLoading(false);
    }
  };

  return (
    <div className="ManageBooking-footer flex flex-col gap-[var(--panelPadding)] border-t-[length:var(--buttonBorderWidth)] border-[var(--pageBackgroundSecondary)] p-[var(--panelPadding)] pb-5 w-full shrink-0 mt-auto">
      <div className="ManageBooking-footer-controls flex items-center justify-start gap-3">
        <div className="ManageBooking-footer-buttons flex gap-2.5">
          {canReopen && (
            <EDSButton
              variant="outline"
              size="md"
              className="ManageBooking-footer-button pr-2.5 [&_.Button__element--solid.Button__element--info]:min-w-[100px]"
              disabled={isReopenLoading}
              onClick={handleUndo}
            >
              Reopen
            </EDSButton>
          )}
          {canRestore && (
            <EDSButton
              variant="outline"
              size="md"
              className="ManageBooking-footer-button pr-2.5 [&_.Button__element--solid.Button__element--info]:min-w-[100px]"
              disabled={isRestoreLoading}
              onClick={handleRestoreBooking}
              aria-busy={isRestoreLoading}
            >
              {isRestoreLoading ? "Restoring…" : "Reopen"}
            </EDSButton>
          )}
          {canReject && (
            <EDSButton
              variant="outline"
              size="md"
              className="ManageBooking-footer-button pr-2.5 [&_.Button__element--solid.Button__element--info]:min-w-[100px]"
              disabled={isRejectLoading}
              onClick={setRejected}
            >
              Reject/Customer Cancelled
            </EDSButton>
          )}
          {canConfirm && (
            <EDSButton
              variant="default"
              size="md"
              className="ManageBooking-footer-button pr-2.5 [&_.Button__element--solid.Button__element--info]:min-w-[100px]"
              disabled={isConfirmLoading}
              onClick={setConfirmed}
            >
              Confirm
            </EDSButton>
          )}
          {canDelete && (
            <EDSButton
              variant="destructive"
              size="md"
              className="ManageBooking-footer-button pr-2.5 [&_.Button__element--solid.Button__element--info]:min-w-[100px]"
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              Delete
            </EDSButton>
          )}
          {!isDeleted && (
            <EDSButton
              variant={noShow ? "default" : "outline"}
              size="md"
              className={[
                "ManageBooking-footer-button pr-2.5 [&_.Button__element--solid.Button__element--info]:min-w-[100px]",
                noShow || selectedBooking.actualGuests === 0
                  ? "ManageBooking-footer-button--selected [&_.Button__element]:bg-button-primary [&_.Button__element]:text-button-primary-foreground [&_.Button__element]:border-0"
                  : ""
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={setNoShow}
            >
              No Show
            </EDSButton>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="double-confirmed"
            checked={selectedBooking.doubleConfirmed ?? false}
            onCheckedChange={setDoubleConfirmed}
            className="h-6 w-6"
          />
          <Label
            htmlFor="double-confirmed"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Double Confirmed
          </Label>
        </div>
      </div>

      {!isDeleted &&
        selectedBooking.actualGuests === 0 &&
        noShowLabels.length > 0 && (
          <div className="NoShowReasons flex flex-col gap-[5px]">
            <span
              className="NoShowReasons-label text-collins-secondary font-collins-primary text-[var(--textColor)]"
              id="no-show-reasons-label"
            >
              Reason for no-show:
            </span>
            <fieldset
              className="NoShowReasons-options flex flex-wrap gap-2 border-none p-0 m-0"
              aria-labelledby="no-show-reasons-label"
            >
              {noShowLabels.map((label) => (
                <button
                  type="button"
                  key={label.id}
                  className={[
                    "NoShowReason-pill cursor-pointer border-2 border-transparent rounded transition-colors duration-200 ease-in-out focus:outline-2 focus:outline-[var(--primaryButtonSolid)] focus:outline-offset-2 bg-transparent p-0",
                    selectedReasons.includes(label.id)
                      ? "NoShowReason-pill--selected border-[var(--primaryButtonSolid)]"
                      : ""
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handleNoShowReasonSelect(label.id)}
                  aria-pressed={selectedReasons.includes(label.id)}
                >
                  <EDSBadge
                    variant="default"
                    className="booking-label-badge"
                    style={
                      {
                        "--booking-type-color": `#${label.colour}`,
                        "--booking-type-foreground": "var(--black)"
                      } as React.CSSProperties
                    }
                  >
                    <span className="booking-label-badge-text">
                      {label.label}
                    </span>
                  </EDSBadge>
                </button>
              ))}
            </fieldset>
          </div>
        )}

      <DeleteBookingDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteBooking}
        isDeleting={isDeleteLoading}
      />
    </div>
  );
};

export default ManageFooter;
